import type { Header } from "@tanstack/react-table";
import type * as React from "react";
import { cn } from "@/shared/lib/utils";

interface DataTableResizeHandleProps<TData, TValue>
	extends Omit<
		React.HTMLAttributes<HTMLDivElement>,
		"onMouseDown" | "onTouchStart"
	> {
	header: Header<TData, TValue>;
}

/**
 * Visual + interactive resize handle for a TanStack header.
 * - 6px hit area, 1px visible divider, expands on hover/active.
 * - Stops event propagation so the header's sort-click handler doesn't fire on drag.
 * - Returns null for non-resizable columns.
 */
export function DataTableResizeHandle<TData, TValue>({
	header,
	className,
	...rest
}: DataTableResizeHandleProps<TData, TValue>) {
	if (!header.column.getCanResize()) return null;
	const isResizing = header.column.getIsResizing();
	const resizeHandler = header.getResizeHandler();

	return (
		// biome-ignore lint/a11y/useKeyWithClickEvents: drag handle — keyboard resizing isn't a target use case
		// biome-ignore lint/a11y/noStaticElementInteractions: same; no native element matches a column resize handle
		<div
			title={`Resize ${header.column.id} column`}
			data-resizing={isResizing || undefined}
			onMouseDown={(e) => {
				e.stopPropagation();
				resizeHandler(e);
			}}
			onTouchStart={(e) => {
				e.stopPropagation();
				resizeHandler(e);
			}}
			onClick={(e) => e.stopPropagation()}
			className={cn(
				// Hit area: 8px wide, centered vertically, full row height area for easy grabbing
				"absolute right-0 top-0 z-10 flex h-full w-2 cursor-col-resize touch-none select-none items-center justify-end",
				// Visible divider: 1px line; widens + colors on hover and during drag
				"after:block after:h-3/5 after:w-px after:bg-border after:transition-[background-color,width] after:duration-100",
				"hover:after:w-0.5 hover:after:bg-foreground/40",
				"data-[resizing]:after:w-0.5 data-[resizing]:after:bg-primary",
				className,
			)}
			{...rest}
		/>
	);
}
